import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, 
  TrendingDown, 
  Calendar, 
  Brain, 
  Heart, 
  Activity,
  Lightbulb,
  AlertCircle,
  Moon
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { calculateLunarPhase, getCyclePhase } from '@/utils/lunarPhases';

interface TrendData {
  date: string;
  cycleDay?: number;
  lunarPhase?: string;
  mood?: string;
  energy?: number;
  symptoms?: string[];
  sleep_quality?: number;
  menstrual_flow?: string;
  menstrual_symptoms?: string[];
  hot_flashes_frequency?: number;
  menopause_symptoms?: string[];
}

interface Pattern {
  type: string;
  description: string;
  frequency: number;
  recommendation: string;
  severity: 'low' | 'medium' | 'high';
  cycleDay?: number;
  lunarPhase?: string;
}

interface TrendsAnalysisProps {
  onClose: () => void;
}

export default function TrendsAnalysis({ onClose }: TrendsAnalysisProps) {
  const [loading, setLoading] = useState(true);
  const [trendData, setTrendData] = useState<TrendData[]>([]);
  const [patterns, setPatterns] = useState<Pattern[]>([]);
  const [selectedTimeframe, setSelectedTimeframe] = useState<'3months' | '6months' | '1year'>('3months');

  useEffect(() => {
    fetchTrendsData();
  }, [selectedTimeframe]);

  const fetchTrendsData = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const months = selectedTimeframe === '3months' ? 3 : selectedTimeframe === '6months' ? 6 : 12;
      const startDate = format(startOfMonth(subMonths(new Date(), months)), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(new Date()), 'yyyy-MM-dd');

      const { data, error } = await supabase
        .from('daily_checkins')
        .select(`
          date,
          menstrual_cycle_day,
          lunar_phase,
          mood_physical_impact,
          sleep_quality,
          menstrual_flow,
          menstrual_symptoms,
          hot_flashes_frequency,
          menopause_symptoms,
          cycle_start_date,
          cycle_length,
          daily_feeling,
          emotion
        `)
        .eq('user_id', user.id)
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: true });

      if (error) throw error;

      const processedData: TrendData[] = data?.map(item => ({
        date: item.date,
        cycleDay: item.menstrual_cycle_day || undefined,
        lunarPhase: item.lunar_phase || calculateLunarPhase(new Date(item.date)).phase,
        mood: item.daily_feeling || item.emotion,
        sleep_quality: item.sleep_quality,
        menstrual_flow: item.menstrual_flow,
        menstrual_symptoms: item.menstrual_symptoms || [],
        hot_flashes_frequency: item.hot_flashes_frequency || 0,
        menopause_symptoms: item.menopause_symptoms || []
      })) || [];

      setTrendData(processedData);
      analyzePatterns(processedData);
    } catch (error) {
      console.error('Error fetching trends data:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzePatterns = (data: TrendData[]) => {
    const foundPatterns: Pattern[] = [];

    // Analyse menopause patterns (new section)
    const menopauseEntries = data.filter(d => d.menopause_symptoms && d.menopause_symptoms.length > 0);
    if (menopauseEntries.length >= 3) {
      // Track period frequency for menopause detection
      const periodsLastYear = data.filter(d => d.menstrual_flow && d.menstrual_flow !== 'none').length;
      const monthsWithPeriods = new Set(data.filter(d => d.menstrual_flow && d.menstrual_flow !== 'none').map(d => d.date.substring(0, 7))).size;
      
      if (periodsLastYear < 4 && monthsWithPeriods < 4) {
        foundPatterns.push({
          type: 'Menopause Milestone',
          description: `You've had fewer than 4 periods in recent months. If this continues for 12 months, you'll have reached menopause.`,
          frequency: periodsLastYear,
          recommendation: 'Track your symptoms closely. Consider consulting with a healthcare provider about hormone changes and support options.',
          severity: 'medium'
        });
      }

      // Analyse lunar phase correlations with menopause symptoms
      const lunarSymptomCorrelations: { [phase: string]: string[] } = {};
      menopauseEntries.forEach(item => {
        if (item.lunarPhase && item.menopause_symptoms) {
          if (!lunarSymptomCorrelations[item.lunarPhase]) lunarSymptomCorrelations[item.lunarPhase] = [];
          lunarSymptomCorrelations[item.lunarPhase].push(...item.menopause_symptoms);
        }
      });

      Object.entries(lunarSymptomCorrelations).forEach(([phase, symptoms]) => {
        const uniqueSymptoms = [...new Set(symptoms)];
        if (symptoms.length >= 3 && uniqueSymptoms.length > 0) {
          let phaseDescription = '';
          let recommendation = '';
          
          switch(phase) {
            case 'new_moon':
              phaseDescription = 'New Moon (new beginnings, introspection)';
              recommendation = 'Use this time for self-reflection and planning. Set intentions for managing symptoms.';
              break;
            case 'full_moon':
              phaseDescription = 'Full Moon (peak energy, emotional intensity)';
              recommendation = 'Expect heightened symptoms. Plan extra self-care and avoid stressful situations when possible.';
              break;
            case 'waxing_crescent':
            case 'waxing_gibbous':
              phaseDescription = 'Waxing Moon (building energy)';
              recommendation = 'Good time for gentle activity and building healthy habits to manage symptoms.';
              break;
            case 'waning_crescent':
            case 'waning_gibbous':
              phaseDescription = 'Waning Moon (releasing energy)';
              recommendation = 'Focus on rest and letting go of stress. Practice relaxation techniques.';
              break;
          }

          foundPatterns.push({
            type: 'Lunar-Menopause Pattern',
            description: `During ${phaseDescription}, you often experience: ${uniqueSymptoms.join(', ')}`,
            frequency: symptoms.length,
            recommendation,
            severity: uniqueSymptoms.includes('Hot Flashes') || uniqueSymptoms.includes('Night Sweats') ? 'high' : 'medium'
          });
        }
      });

      // Predict upcoming challenging periods
      const recentData = data.slice(-14); // Last 2 weeks
      const symptomTrends = recentData.reduce((acc, item) => {
        if (item.menopause_symptoms) {
          item.menopause_symptoms.forEach(symptom => {
            acc[symptom] = (acc[symptom] || 0) + 1;
          });
        }
        return acc;
      }, {} as { [symptom: string]: number });

      const increasingSymptoms = Object.entries(symptomTrends)
        .filter(([_, count]) => count >= 3)
        .map(([symptom]) => symptom);

      if (increasingSymptoms.length > 0) {
        foundPatterns.push({
          type: 'Menopause Prediction',
          description: `Based on recent patterns, you may experience increased ${increasingSymptoms.join(', ').toLowerCase()} in the coming days.`,
          frequency: Math.max(...Object.values(symptomTrends)),
          recommendation: getSymptomSpecificAdvice(increasingSymptoms),
          severity: 'medium'
        });
      }
    }

    // Analyse menstrual cycle patterns
    const menstrualSymptoms = data.filter(d => d.menstrual_symptoms && d.menstrual_symptoms.length > 0);
    if (menstrualSymptoms.length >= 3) {
      const cycleDayGroups: { [key: number]: string[] } = {};
      
      menstrualSymptoms.forEach(item => {
        if (item.cycleDay) {
          if (!cycleDayGroups[item.cycleDay]) cycleDayGroups[item.cycleDay] = [];
          cycleDayGroups[item.cycleDay].push(...(item.menstrual_symptoms || []));
        }
      });

      Object.entries(cycleDayGroups).forEach(([cycleDay, symptoms]) => {
        const uniqueSymptoms = [...new Set(symptoms)];
        const frequency = symptoms.length;
        
        if (frequency >= 3 && uniqueSymptoms.length > 0) {
          let recommendation = '';
          let severity: 'low' | 'medium' | 'high' = 'low';

          if (parseInt(cycleDay) <= 5) { // Menstruation
            recommendation = 'Schedule rest days around your period. Consider iron-rich foods and gentle movement.';
            severity = symptoms.includes('Heavy bleeding') ? 'high' : 'medium';
          } else if (parseInt(cycleDay) >= 15) { // Luteal phase
            recommendation = 'Plan lighter activities in your luteal phase. Focus on stress management and magnesium supplements.';
            severity = symptoms.includes('Severe cramps') ? 'high' : 'medium';
          }

          foundPatterns.push({
            type: 'Menstrual Cycle Pattern',
            description: `On cycle day ${cycleDay}, you frequently experience: ${uniqueSymptoms.join(', ')}`,
            frequency,
            recommendation,
            severity,
            cycleDay: parseInt(cycleDay)
          });
        }
      });
    }

    // Analyse lunar phase patterns
    const lunarMoodData = data.filter(d => d.lunarPhase && d.mood);
    if (lunarMoodData.length >= 5) {
      const lunarGroups: { [key: string]: string[] } = {};
      
      lunarMoodData.forEach(item => {
        if (item.lunarPhase && item.mood) {
          if (!lunarGroups[item.lunarPhase]) lunarGroups[item.lunarPhase] = [];
          lunarGroups[item.lunarPhase].push(item.mood);
        }
      });

      Object.entries(lunarGroups).forEach(([phase, moods]) => {
        if (moods.length >= 3) {
          const lowEnergyMoods = moods.filter(mood => 
            mood.toLowerCase().includes('tired') || 
            mood.toLowerCase().includes('low') || 
            mood.toLowerCase().includes('fatigue')
          );

          if (lowEnergyMoods.length >= 2) {
            foundPatterns.push({
              type: 'Lunar Energy Pattern',
              description: `During ${phase} moon, you often feel low energy or fatigued`,
              frequency: lowEnergyMoods.length,
              recommendation: 'Plan restful activities during this lunar phase. Consider meditation, gentle yoga, or early bedtime.',
              severity: 'medium',
              lunarPhase: phase
            });
          }
        }
      });
    }

    // Analyse sleep patterns
    const sleepData = data.filter(d => d.sleep_quality !== undefined && d.sleep_quality !== null);
    if (sleepData.length >= 7) {
      const poorSleepDays = sleepData.filter(d => d.sleep_quality && d.sleep_quality <= 3);
      const poorSleepPercentage = (poorSleepDays.length / sleepData.length) * 100;

      if (poorSleepPercentage >= 30) {
        foundPatterns.push({
          type: 'Sleep Quality Pattern',
          description: `Poor sleep quality occurs ${Math.round(poorSleepPercentage)}% of tracked days`,
          frequency: poorSleepDays.length,
          recommendation: 'Consider establishing a consistent bedtime routine, limiting screen time before bed, and creating a cool, dark sleep environment.',
          severity: poorSleepPercentage >= 50 ? 'high' : 'medium'
        });
      }
    }

    // Analyse menopause symptoms (hot flashes)
    const hotFlashData = data.filter(d => d.hot_flashes_frequency && d.hot_flashes_frequency > 0);
    if (hotFlashData.length >= 5) {
      const averageFlashes = hotFlashData.reduce((sum, d) => sum + (d.hot_flashes_frequency || 0), 0) / hotFlashData.length;
      
      foundPatterns.push({
        type: 'Hot Flash Pattern',
        description: `Average of ${Math.round(averageFlashes)} hot flashes per day when occurring`,
        frequency: hotFlashData.length,
        recommendation: 'Track triggers like caffeine, alcohol, or stress. Consider layered clothing and cooling techniques. Consult healthcare provider if severe.',
        severity: averageFlashes >= 5 ? 'high' : averageFlashes >= 3 ? 'medium' : 'low'
      });
    }

    setPatterns(foundPatterns);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'text-red-600 bg-red-50 dark:bg-red-900/20';
      case 'medium': return 'text-orange-600 bg-orange-50 dark:bg-orange-900/20';
      default: return 'text-green-600 bg-green-50 dark:bg-green-900/20';
    }
  };

  const getSymptomSpecificAdvice = (symptoms: string[]): string => {
    const advice: string[] = [];
    
    if (symptoms.includes('Hot Flashes') || symptoms.includes('Night Sweats')) {
      advice.push('Keep a cooling spray handy, dress in layers, avoid triggers like spicy foods and alcohol');
    }
    if (symptoms.includes('Sleep Problems')) {
      advice.push('maintain a cool bedroom, establish a calming bedtime routine, limit caffeine after 2pm');
    }
    if (symptoms.includes('Mood Swings') || symptoms.includes('Anxiety')) {
      advice.push('practice deep breathing, consider meditation, ensure regular exercise and social connection');
    }
    if (symptoms.includes('Fatigue')) {
      advice.push('prioritise rest, plan lighter activities, consider vitamin B complex and iron levels check');
    }
    if (symptoms.includes('Brain Fog')) {
      advice.push('make lists, break tasks into smaller steps, ensure adequate sleep and hydration');
    }
    
    return advice.length > 0 ? advice.join('. ') : 'Focus on self-care, rest, and gentle movement.';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-4xl">
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Analysing your wellness patterns...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Wellness Trends & Patterns
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Discover patterns in your cycle, mood, and symptoms to predict and prepare for future needs
              </p>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClose}
              className="hover:bg-destructive/10 hover:text-destructive"
            >
              ✕ Close
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          <Tabs defaultValue="patterns" className="space-y-6">
            <div className="flex items-center justify-between">
              <TabsList>
                <TabsTrigger value="patterns">Patterns Detected</TabsTrigger>
                <TabsTrigger value="insights">Predictive Insights</TabsTrigger>
                <TabsTrigger value="recommendations">Personalised Tips</TabsTrigger>
              </TabsList>

              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">Timeframe:</label>
                <select 
                  value={selectedTimeframe}
                  onChange={(e) => setSelectedTimeframe(e.target.value as any)}
                  className="text-sm border border-border rounded px-2 py-1 bg-background"
                >
                  <option value="3months">Last 3 months</option>
                  <option value="6months">Last 6 months</option>
                  <option value="1year">Last year</option>
                </select>
              </div>
            </div>

            <TabsContent value="patterns" className="space-y-4">
              {patterns.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Brain className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">Not enough data yet</h3>
                    <p className="text-muted-foreground">
                      Keep tracking for at least 2-3 cycles to start seeing meaningful patterns.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {patterns.map((pattern, index) => (
                    <Card key={index}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {pattern.type.includes('Menstrual') && <Heart className="h-4 w-4 text-red-500" />}
                            {pattern.type.includes('Lunar') && <Moon className="h-4 w-4 text-purple-500" />}
                            {pattern.type.includes('Sleep') && <Activity className="h-4 w-4 text-blue-500" />}
                            {pattern.type.includes('Hot Flash') && <AlertCircle className="h-4 w-4 text-orange-500" />}
                            <span className="font-medium">{pattern.type}</span>
                          </div>
                          <Badge className={getSeverityColor(pattern.severity)}>
                            {pattern.severity} priority
                          </Badge>
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-3">{pattern.description}</p>
                        
                        <div className="bg-muted/50 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Lightbulb className="h-4 w-4 text-primary" />
                            <span className="font-medium text-sm">Recommendation:</span>
                          </div>
                          <p className="text-sm">{pattern.recommendation}</p>
                        </div>
                        
                        <div className="mt-3 text-xs text-muted-foreground">
                          Frequency: {pattern.frequency} occurrences in {selectedTimeframe.replace('months', ' months').replace('year', ' year')}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="insights" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Coming Up: Predictive Insights</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {patterns
                    .filter(p => p.cycleDay || p.lunarPhase)
                    .slice(0, 3)
                    .map((pattern, index) => (
                      <div key={index} className="border-l-4 border-primary pl-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="h-4 w-4" />
                          <span className="font-medium text-sm">
                            {pattern.cycleDay ? `Cycle Day ${pattern.cycleDay}` : `${pattern.lunarPhase} Moon`}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          Based on your patterns, you may experience: {pattern.description.split(':')[1]?.trim() || pattern.description}
                        </p>
                        <p className="text-sm font-medium text-primary">
                          Prepare by: {pattern.recommendation}
                        </p>
                      </div>
                    ))}
                  
                  {patterns.filter(p => p.cycleDay || p.lunarPhase).length === 0 && (
                    <div className="text-center py-8">
                      <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        Continue tracking to unlock predictive insights about your upcoming cycle phases.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="recommendations" className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                {patterns.map((pattern, index) => (
                  <Card key={index}>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Lightbulb className="h-4 w-4" />
                        {pattern.type}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="text-sm text-muted-foreground">
                          {pattern.description}
                        </div>
                        <div className="bg-primary/5 rounded-lg p-3">
                          <p className="text-sm font-medium text-primary">
                            {pattern.recommendation}
                          </p>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>Priority: {pattern.severity}</span>
                          <span>{pattern.frequency} times tracked</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {patterns.length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Lightbulb className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">Keep tracking to unlock insights</h3>
                    <p className="text-muted-foreground">
                      Your personalised recommendations will appear here as we detect patterns in your data.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}